import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { IIdName, IIdNameWithApiValue } from '@/interfaces/general/IIdName.ts';
import { IDashboardCommonSettings } from '@/interfaces/powerMarkets/dashboard/IDashboard.ts';
import { type IBatteryInputData, IBatteryInputDataDefaults } from '@/interfaces/storageForecasting/IBatteryInputData.ts';
import { IAssetOptions } from '@/interfaces/storageForecasting/IAssetOptions.ts';
import { hoursInDay, monthsInYear } from '@/utils/storageForecasting/assetValidationUtils.ts';
import { TStorageForecastCountries } from '@/types/TStorageForecastCountries.ts';

dayjs.extend(utc);

export const TEMP_BLOCKED_WIND_SOLAR_STANDALONE_QUARTERS_BY_COUNTRY: Record<TStorageForecastCountries, Array<string>> = {
    GB: ['2025Q3', '2025Q4'],
    Germany: [],
};

export const ASSET_PRESET_COUNTRIES: Array<IIdName> = [
    { id: 'GB', name: 'Great Britain' },
    { id: 'Germany', name: 'Germany' },
];

export const LEGACY_ASSET_GROUP_NAME_GB = 'My First GB Group';
export const LEGACY_ASSET_GROUP_NAME_GERMANY = 'My First Germany Group';

export const ASSET_READ_ONLY_MESSAGE = 'Access is limited to results viewing only for this country. Please upgrade to full access for complete functionality.';

export const MAX_ASSETS_MESSAGE = 'Maximum number of assets reached';

export const OUTPUTS_VIEW_DEFAULT_BLOCK_MESSAGE = 'Viewing outputs requires Long Term Power - Outputs & Long Term Power - Comparison access.';

export const MAX_COMPARISON_ASSETS = 4;
export const MAX_BULK_BALANCE_SHEET_DOWNLOAD_RUNS = 10;
export const DEFAULT_ASSET_ICON_COLOUR = 'var(--lcpBlue)';
export const DEFAULT_ASSET_ICON_ID = 'battery-bolt';

export const DEFAULT_STORECAST_SESSION_CHART_OVERRIDES: Readonly<IDashboardCommonSettings> = {
    sharedTooltipAcrossModules: false,
    sharedLegendToggling: false,
    useYAxisNormalisation: true,
};

export const DEFAULT_PERCENTILES: Array<IIdNameWithApiValue> = [
    { id: 'mean', name: 'Mean', apiValue: 'Mean' },
    { id: 'min', name: 'Min', apiValue: 'Min' },
    { id: 'p25', name: 'P25', apiValue: 'P25' },
    { id: 'p75', name: 'P75', apiValue: 'P75' },
    { id: 'max', name: 'Max', apiValue: 'Max' },
];

export const DEFAULT_BATTERY_VALUES: IBatteryInputDataDefaults = {
    defaultRampRateRestriction: 40,
    defaultCfdContractLength: 15,
    defaultFrCapacityRestriction: 100,
};

export const DEFAULT_ASSET_OPTIONS: IAssetOptions = {
    period: 'calendarYearly',
    yearStarting: dayjs().year().toString(),
};

function createDefaultNetworkAvailabilityMatrix (): number[][] {
    return Array.from({ length: monthsInYear }, () => Array.from({ length: hoursInDay }, () => 1));
}

function createDefaultAssetInputBase (): Omit<IBatteryInputData, 'country' | 'canRunFr' | 'regionToGrabFromDb'> {
    return {
        allowInterdayCycling: false,
        applyCustomResLoadFactor: false,
        applyMaxCyclesPerDay: false,
        applyRampRatesToAllStageFr: false,
        applyRampRatesToTwoStageFr: false,
        assetHasCfd: false,
        assetPolicySupportType: 'None',
        cfdContractLength: DEFAULT_BATTERY_VALUES.defaultCfdContractLength,
        cfdContractStart: dayjs.utc().startOf('year').toISOString(),
        cfdStrikePrice: {
            values: [0],
            granularity: 'Year',
        },
        chargeCapacity: {
            values: [0],
            granularity: 'Year',
        },
        cmContract: 'None',
        cmContractStart: dayjs().startOf('month').toISOString(),
        competeInAncillaries: true,
        competeInBalancing: true,
        competeInDayAhead: true,
        competeInIntraday: true,
        competeInLocationalBalancing: true,
        competeInTwoStageFr: false,
        coupling: 'AC',
        customResLoadFactor: {
            values: [0],
            granularity: 'Year',
        },
        dischargeCapacity: {
            values: [0],
            granularity: 'Year',
        },
        duration: {
            values: [0],
            granularity: 'Year',
        },
        duosCapacityCharge: {
            values: [0],
            granularity: 'Year',
        },
        duosExportCredit: {
            values: [0],
            granularity: 'Year',
        },
        duosImportCharge: {
            values: [0],
            granularity: 'Year',
        },
        efficiency: {
            values: [0],
            granularity: 'Year',
        },
        endDateInclusive: dayjs.utc().add(1, 'year').startOf('year').toISOString(),
        exportCapacity: {
            values: [0],
            granularity: 'Year',
        },
        frCapacityRestrictionMode: 'Binary',
        gridConnection: null,
        id: null,
        importCapacity: {
            values: [0],
            granularity: 'Year',
        },
        inverterCapacityBattery: {
            values: [0],
            granularity: 'Year',
        },
        inverterCapacityOnsiteGen: {
            values: [0],
            granularity: 'Year',
        },
        inverterEfficiencyBattery: {
            values: [100],
            granularity: 'Year',
        },
        inverterEfficiencyOnsiteGen: {
            values: [100],
            granularity: 'Year',
        },
        locationalBalancingRegion: null,
        maxAverageCycles: {
            values: [0],
            granularity: 'Year',
        },
        maxCyclesPerDay: {
            values: [0],
            granularity: 'Year',
        },
        maxPercentageCapacityAllowedInAllStageFr: {
            values: [DEFAULT_BATTERY_VALUES.defaultFrCapacityRestriction],
            granularity: 'Year',
        },
        maxPercentageCapacityAllowedInFrMarketsCombined: {
            values: [DEFAULT_BATTERY_VALUES.defaultFrCapacityRestriction],
            granularity: 'Year',
        },
        maxPercentageCapacityAllowedInTwoStageFr: {
            values: [DEFAULT_BATTERY_VALUES.defaultFrCapacityRestriction],
            granularity: 'Year',
        },
        minimumSpread: {
            values: [0],
            granularity: 'Year',
        },
        name: '',
        networkExportAvailability: { values: createDefaultNetworkAvailabilityMatrix() },
        networkImportAvailability: { values: createDefaultNetworkAvailabilityMatrix() },
        onsiteCapacity: {
            values: [0],
            granularity: 'Year',
        },
        onsiteSolarCapacity: {
            values: [0],
            granularity: 'Year',
        },
        onsiteWindCapacity: {
            values: [0],
            granularity: 'Year',
        },
        overrideDuos: false,
        refurbishAge: null,
        runAsLongDuration: false,
        runLocationalBalancing: true,
        siteHasRampRestrictions: false,
        siteRampDownRestriction: {
            values: [DEFAULT_BATTERY_VALUES.defaultRampRateRestriction],
            granularity: 'Year',
        },
        siteRampUpRestriction: {
            values: [DEFAULT_BATTERY_VALUES.defaultRampRateRestriction],
            granularity: 'Year',
        },
        startDate: dayjs().startOf('year').toISOString(),
        storecastOnsiteOption: 'Solar',
        tlfZone: null,
        tnuosZone: null,
        vom: {
            values: [0],
            granularity: 'Year',
        },
    };
}

export const DEFAULT_GB_ASSET_CONFIGURATION: IBatteryInputData = {
    ...createDefaultAssetInputBase(),
    country: 'GB',
    canRunFr: true,
    regionToGrabFromDb: 'Southern',
};

export const DEFAULT_GERMAN_ASSET_CONFIGURATION: IBatteryInputData = {
    ...createDefaultAssetInputBase(),
    country: 'Germany',
    canRunFr: false,
    regionToGrabFromDb: 'Central',
};
